import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { Theme, useTheme } from "@mui/material/styles";
import ExamAttemptQuestionScore from "components/ExamAttemptQuestionScore";
import { FormattedMessage as T } from "react-intl";
import { ExamAttemptQuestion } from "services/KnowfullyAdminClient";
import { QuestionTypeEnum } from "types/common-types";

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    height: "100%",
    padding: "15px 15px",
  },
}));

type ExamQuestionDetailsDialogProps = {
  open: boolean;
  examAttemptQuestion: ExamAttemptQuestion;
  onClose: () => void;
};

const ExamQuestionDetailsDialog = ({
  open,
  examAttemptQuestion,
  onClose,
}: ExamQuestionDetailsDialogProps) => {
  const styles = useStyles(useTheme());

  const isManualGrading =
    examAttemptQuestion.questionType?.value === QuestionTypeEnum.TextQuestion;

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle>
        <T id="questionLabel" /> (
        <T
          id="questionTypeShort"
          values={{ type: examAttemptQuestion.questionType?.name ?? "-" }}
        />
        )
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          dangerouslySetInnerHTML={{
            __html: examAttemptQuestion.title ?? "",
          }}
        />
        <Box my={2}>
          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={12} sm={12} md={6}>
              <Paper sx={styles.paper}>
                <Box paddingBottom="5px">
                  <Typography variant="h5">
                    <T id="userAnswerLabel" />
                  </Typography>
                </Box>
                {!isManualGrading && (
                  <ol
                    type="A"
                    start={
                      examAttemptQuestion.answers!.findIndex(
                        (x) =>
                          x.id ===
                          examAttemptQuestion.userAnswers?.find(
                            (y) => y.id === x.id
                          )?.id
                      ) + 1
                    }
                  >
                    {examAttemptQuestion.userAnswers?.map((userAnswer) => (
                      <li
                        key={userAnswer.id}
                        dangerouslySetInnerHTML={{
                          __html: userAnswer.value ?? "",
                        }}
                      ></li>
                    ))}
                  </ol>
                )}
                {isManualGrading && (
                  <Typography>
                    {examAttemptQuestion.userAnswers?.map((userAnswer) => (
                      <p key={userAnswer.id}>{userAnswer.value}</p>
                    ))}
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Paper sx={styles.paper}>
                <Box paddingBottom="5px">
                  <Typography variant="h5">
                    <T id="expectedAnswerLabel" />
                  </Typography>
                </Box>
                {!isManualGrading && (
                  <ol type="A">
                    {examAttemptQuestion.answers?.map((a) => (
                      <li
                        key={a.id}
                        style={{
                          textDecoration: a.isAnswer ? "underline" : "unset",
                        }}
                        dangerouslySetInnerHTML={{ __html: a.value ?? "" }}
                      ></li>
                    ))}
                  </ol>
                )}
                {isManualGrading && (
                  <Typography>
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          examAttemptQuestion.answers?.find((x) => !!x.isAnswer)
                            ?.value ?? "",
                      }}
                    ></div>
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Typography variant="h6">
            <T id="scoreLabel" />
          </Typography>
          <ExamAttemptQuestionScore question={examAttemptQuestion} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" data-cy="closeButton">
          <T id="closeButton" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExamQuestionDetailsDialog;
